import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { ensureArray } from '@/lib/utils/ensure-array';
import type {
  TtsFlowComposeRequest,
  TtsFlowDetailResponse,
  TtsFlowListRow,
  TtsFlowUpdateRequest,
  TtsLogRequest,
  TtsLogRow,
  AdminTtsFlowDetail,
  TtsFlowReviewAction,
  BulkDeleteTtsFlowsResponse,
  BulkReviewTtsFlowsResponse,
  TtsFlowLogRow,
} from './types';
import { voiceComposeKeys } from '../voice-campaigns/api';
import { useUserProfile } from '@/features/auth/api';
import { useCurrentUserModules } from '@/lib/permissions/use-current-modules';

/** TTS script gate: module 7/10 AND TTS balance > 0. UI only, API enforces too. */
export function useTtsAccess(): {
  blocked: boolean;
  reason: 'no-module' | 'low-balance' | null;
  message: string | null;
} {
  const { modules, isLoading } = useCurrentUserModules();
  const profile = useUserProfile();
  if (modules === null || isLoading || profile.isLoading) {
    return { blocked: false, reason: null, message: null };
  }
  if (!modules.has(7) && !modules.has(10)) {
    return {
      blocked: true,
      reason: 'no-module',
      message: 'TTS module is not enabled for your account. Please contact your admin.',
    };
  }
  if (!(Number(profile.data?.ttsCredits ?? 0) > 0.001)) {
    return {
      blocked: true,
      reason: 'low-balance',
      message: 'Your TTS balance is 0. Please contact your manager to recharge.',
    };
  }
  return { blocked: false, reason: null, message: null };
}

/** `{ message }` from an API error response, else the fallback. */
export function apiErrorMessage(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  return typeof msg === 'string' && msg ? msg : fallback;
}

export const ttsFlowKeys = {
  all: ['tts-flow'] as const,
  adminList: ['tts-flow', 'admin', 'list'] as const,
  adminListApproved: ['tts-flow', 'admin', 'list', 'approved'] as const,
  list: (userId: string | number | null) => ['tts-flow', 'list', userId] as const,
  detail: (id: string | number) => ['tts-flow', 'detail', id] as const,
  logsCont: (userId: string | number | null, start: string, end: string) =>['tts-flow', 'cont','logs', userId, start, end] as const,
  logsTTS: (userId: string | number | null, start: string, end: string) =>['tts-flow', 'logs', userId, start, end] as const,
  adminDetail: (id: string | number | null) => ['tts-flow', 'admin', 'detail', id] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: voiceComposeKeys.all });
    qc.invalidateQueries({ queryKey: ttsFlowKeys.all });
  };
}

/** GET /api/obd/tts/flow/admin/list */
export function useAdminTtsFlowList() {
  return useQuery({
    queryKey: ttsFlowKeys.adminList,
    queryFn: () => api.get<TtsFlowListRow[]>('/api/obd/tts/flow/admin/list'),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsFlowListRow>(data),
  });
}

/** GET /api/obd/tts/flow/admin/list */
export function useAdminApprovedTtsFlowList() {
  return useQuery({
    queryKey: ttsFlowKeys.adminListApproved,
    queryFn: () => api.get<TtsFlowListRow[]>('/api/obd/tts/flow/admin/approved/list'),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsFlowListRow>(data),
  });
}

/** GET /api/obd/tts/flow/admin/${id} */
export function useAdminOrUserTtsFlowDetail(id: string | number | null) {
  const isSubOrSuperAdmin = useAuthStore((r) => r.isSubOrSuperAdmin);
  return useQuery({
    queryKey: ttsFlowKeys.adminDetail(id ?? 'none'),
    queryFn: () =>
      api.get<AdminTtsFlowDetail>(
        isSubOrSuperAdmin() ? `/api/obd/tts/flow/admin/${id}` : `/api/obd/tts/flow/detail/${id}`,
      ),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** /api/obd/tts/flow/admin/${p.id} — sets status 1 (approved) / 2 (rejected). */
export function useReviewTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: { id: string | number; action: TtsFlowReviewAction }) =>
      api.post<{ message?: string }>(`/api/obd/tts/flow/admin/${p.id}`, {
        action: p.action,
      }),
    onSuccess: invalidate,
  });
}

/** GET /api/obd/tts/flow/list/{userId} */
export function useTtsFlowList() {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ttsFlowKeys.list(userId),
    queryFn: () => api.get<TtsFlowListRow[]>(`/api/obd/tts/flow/list/${userId}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsFlowListRow>(data),
  });
}

/** GET /api/obd/get/tts/flow/{ttsFlowId} */
export function useTtsFlowDetail(id: string | number | null) {
  return useQuery({
    queryKey: ttsFlowKeys.detail(id ?? 'none'),
    queryFn: () => api.get<TtsFlowDetailResponse>(`/api/obd/get/tts/flow/${id}`),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** POST /api/obd/add/tts/flow */
export function useCreateTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TtsFlowComposeRequest) =>
      api.post<{ message?: string }>('/api/obd/add/tts/flow', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/update/tts/flow — same shape + `id`. */
export function useUpdateTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TtsFlowUpdateRequest) =>
      api.post<{ message?: string,status?:string }>('/api/obd/update/tts/flow', body),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/get/continuous/logs — TTS campaign run logs.
 *  Body: `{ userId, startDate, endDate }` (YYYY-MM-DD). */
export function useTtsLogs(startDate: string, endDate: string) {
  const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ttsFlowKeys.logsCont(userId, startDate, endDate),
    queryFn: () =>
      api.post<TtsLogRow[]>('/api/obd/get/continuous/logs', {
        userId: Number(userId),
        startDate,
        endDate,
      } as TtsLogRequest),
    enabled: Boolean(userId && startDate && endDate),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsLogRow>(data),
  });
}

export function useTtsFlowLogs(startDate: string, endDate: string){
   const userId = useAuthStore((s) => s.userId);
  return useQuery({
    queryKey: ttsFlowKeys.logsTTS(userId, startDate, endDate),
    queryFn: () =>
      api.post<TtsFlowLogRow[]>('/api/obd/get/tts-flow/logs', {
        userId: Number(userId),
        startDate,
        endDate,
      } as TtsLogRequest),
    enabled: Boolean(userId && startDate && endDate),
    staleTime: 30_000,
    select: (data) => ensureArray<TtsFlowLogRow>(data),
  });
}

/** DELETE /api/obd/tts-flow/{id} — legacy uses POST-as-DELETE; mirror that. */
export function useDeleteTtsFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ message?: string }>(`/api/obd/tts-flow/${id}`),
    onSuccess: invalidate,
  });
}

/** DELETE /api/obd/tts-flow — bulk hard-delete. Body: { ttsIds: string[] } */
export function useBulkDeleteTtsFlows() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (ttsIds: (string | number)[]) =>
      api.delete<BulkDeleteTtsFlowsResponse>('/api/obd/tts-flow/bulk', {
        data: { ttsIds },
      }),
    onSuccess: invalidate,
  });
}

/** POST /api/obd/tts-flow/admin/bulk — bulk approve/reject. */
export function useBulkReviewTtsFlows() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (p: { ids: (string | number)[]; action: TtsFlowReviewAction }) =>
      api.post<BulkReviewTtsFlowsResponse>('/api/obd/tts-flow/admin/bulk', {
        ids: p.ids,
        action: p.action,
      }),
    onSuccess: invalidate,
  });
}

